import { Component, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { ActivatedRoute, Params, Router, RouterLinkActive, RouterLink } from '@angular/router'
import { Observable, Subscription } from 'rxjs'
import { CollectionService } from '@services/collection.service'
import { ToastService } from '@services/toast.service'
import { CollectionForm } from '@models/collections/collection-form'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import { Toast } from '@models/translations/toast'
import { TranslateService, TranslatePipe } from '@ngx-translate/core'
import { storageKeys } from '@constants/storage-keys'
import {
  CollectionBasicDetailsTabComponent,
  CollectionEducationalDetailsTabComponent,
  CollectionMaterialsTabComponent,
  CollectionPreviewTabComponent
} from './index'
import { FocusRemoverDirective } from '../../directives/focus-remover.directive'
import { CollectionBasicDetailsTabComponent as CollectionBasicDetailsTabComponent_1 } from './collection-basic-details-tab/collection-basic-details-tab.component'
import { CollectionEducationalDetailsTabComponent as CollectionEducationalDetailsTabComponent_1 } from './collection-educational-details-tab/collection-educational-details-tab.component'
import { CollectionMaterialsTabComponent as CollectionMaterialsTabComponent_1 } from './collection-materials-tab/collection-materials-tab.component'
import { CollectionPreviewTabComponent as CollectionPreviewTabComponent_1 } from './collection-preview-tab/collection-preview-tab.component' // @todo: this might need rework

@Component({
  selector: 'app-collection-form',
  templateUrl: './collection-form.component.html',
  styleUrls: ['./collection-form.component.scss'],
  imports: [
    FocusRemoverDirective,
    RouterLinkActive,
    RouterLink,
    CollectionBasicDetailsTabComponent_1,
    CollectionEducationalDetailsTabComponent_1,
    CollectionMaterialsTabComponent_1,
    CollectionPreviewTabComponent_1,
    TranslatePipe
  ]
})
export class CollectionFormComponent implements OnInit, OnDestroy {
  collectionId: string
  tabId: number
  routeSubscription: Subscription
  collection: CollectionForm
  collectionSubscription: Subscription
  confirmAbortModalRef: BsModalRef
  noPermissionTitle: string
  noPermissionMessage: string
  abortMessage: string
  @ViewChild(CollectionBasicDetailsTabComponent) basicTab: CollectionBasicDetailsTabComponent
  @ViewChild(CollectionEducationalDetailsTabComponent)
  educationalTab: CollectionEducationalDetailsTabComponent
  @ViewChild(CollectionMaterialsTabComponent) materialsTab: CollectionMaterialsTabComponent
  @ViewChild(CollectionPreviewTabComponent) previewTab: CollectionPreviewTabComponent

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private collectionSvc: CollectionService,
    private toastr: ToastService,
    private modalSvc: BsModalService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.collectionId = this.route.snapshot.paramMap.get('collectionId')

    this.translate.get('forms.collection.toasts.noPermission').subscribe((translation: Toast) => {
      this.noPermissionTitle = translation.title
      this.noPermissionMessage = translation.message
    })

    this.translate
      .get('forms.editEducationalResource.abort.text')
      .subscribe((translation: string) => {
        this.abortMessage = translation
      })

    this.routeSubscription = this.route.paramMap.subscribe((params: Params) => {
      this.tabId = +params.get('tabId')

      if (!this.tabId) {
        return this.router.navigate(['/kokoelma', this.collectionId, 'muokkaa', 1])
      }
    })

    this.collectionSubscription = this.collectionSvc.editCollection$.subscribe(
      (collection: CollectionForm) => {
        this.collection = collection

        if (JSON.stringify(collection) === '{}') {
          // @todo: replace with translation strings
          // eslint-disable-next-line max-len
          this.toastr.error(this.noPermissionMessage, this.noPermissionTitle)

          return this.router.navigate(['/etusivu'])
        }
      }
    )
    this.collectionSvc.updateEditCollection(this.collectionId)
  }

  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe()
    this.collectionSubscription.unsubscribe()

    sessionStorage.removeItem(storageKeys.collection)
  }

  /**
   * Shows confirm modal for abort.
   * @param {TemplateRef<any>} template
   */
  openConfirmAbortModal(template: TemplateRef<any>): void {
    this.confirmAbortModalRef = this.modalSvc.show(template)
  }

  /**
   * Removes collection from session storage. Redirects user to user materials view.
   */
  abort(): void {
    sessionStorage.removeItem(storageKeys.collection)

    this.confirmAbortModalRef.hide()

    void this.router.navigate(['/omat-oppimateriaalit'])
  }

  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    if (
      this.basicTab?.form.dirty ||
      this.educationalTab?.form.dirty ||
      this.materialsTab?.form.dirty
    ) {
      return confirm(this.abortMessage)
    }

    if (this.previewTab?.canDeactivate) {
      return true
    }

    const changedCollection: CollectionForm = JSON.parse(
      sessionStorage.getItem(storageKeys.collection)
    )

    if (changedCollection) {
      return changedCollection === this.collection ? true : confirm(this.abortMessage)
    }

    return true
  }
}
